"use client";

import Button from "@afa-shared/afa-components/dist/Buttons/Button";
import FactCard from "@afa-shared/afa-components/dist/FactCard";
import Heading from "@afa-shared/afa-components/dist/Heading";
import Checkbox from "@afa-shared/afa-components/dist/Inputs/Checkbox";
import Select from "@afa-shared/afa-components/dist/Inputs/Select";
import TextArea from "@afa-shared/afa-components/dist/Inputs/TextArea";
import TextInput from "@afa-shared/afa-components/dist/Inputs/TextInput";
import Text from "@afa-shared/afa-components/dist/Text";
import { FormProps } from "@components/FormBlock";
import { StandardLink } from "@components/Link/StandardLink";
import Grid from "@components/client/Grid";
import clsx from "clsx";
import { parseAsBoolean, useQueryState } from "nuqs";
import { useEffect, useId, useRef, useState } from "react";
import { GoogleReCaptcha } from "react-google-recaptcha-v3";

import FormErrorFeedback from "../Feedback/FormErrorFeedback";
import { FormSendFeedback } from "../Feedback/FormSendFeedback";
import FormSuccessFeedback from "../Feedback/FormSuccessFeedback";
import FormValidationFeedback from "../Feedback/FormValidationFeedback";
import formStyles from "../FormStyles/forms.module.css";
import { emailMaxInputLength, messageMaxInputLength } from "../formConstants";
import { scrollIntoView, validate } from "../formHelper";
import { trackFormStart, trackFormSubmit } from "../formTracker";
import { formData } from "./ContactFormData";

export const ContactForm = ({ contentfulPreviewEntryId, formType }: FormProps) => {
  const personuppgifterPath = "/personuppgifter";
  const caseTypes = formData.subjects[formType];

  const [formStarted, setFormStarted] = useState(false);
  const [previewing, setPreviewing] = useQueryState("preview", parseAsBoolean);
  const [isLoading, setIsLoading] = useState(false);
  const [displayForm, setDisplayForm] = useState<boolean>(true);
  const [isValidToken, setIsValidToken] = useState<boolean>(false);
  const [errorSendMail, setErrorSendMail] = useState<boolean>(false);
  const [hasSingleError, setHasSingleError] = useState<boolean>(false);
  const [formState, setFormState] = useState({
    email: { value: "", required: true },
    contactFormCaseType: { value: formData.caseTypeInitValue[formType], required: true },
    contactFormMessage: { value: "", required: true },
    sendCopy: { value: false, required: false },
    formType: { value: formType, required: false },
  });
  const [errors, setErrors] = useState<Record<string, any>>({
    email: null,
    contactFormCaseType: null,
    contactFormMessage: null,
    approved: null,
  });

  const formRefs = {
    sendFeedback: useRef(null),
    errorInfobox: useRef(null),
    email: useRef(null),
    contactFormCaseType: useRef(null),
    contactFormMessage: useRef(null),
    sendCopy: useRef(null),
    previewRef: useRef(null),
  };

  const formIds = {
    email: useId(),
    contactFormCaseType: useId(),
    contactFormMessage: useId(),
    header: useId(),
    sendCopy: useId(),
  };

  useEffect(() => {
    if (previewing) {
      const formErrors = validate(formState, errors);
      const foundErrors = Object.values(formErrors).filter((e) => e !== null).length;
      if (foundErrors) {
        setPreviewing(false);
      } else {
        setPreviewing(true);
      }
    } else {
      setPreviewing(false);
    }
  }, [errors, formState, previewing]);

  const goToPreview = (evt: any) => {
    evt.preventDefault();
    setHasSingleError(false);
    const formErrors = validate(formState, errors);
    setErrors(formErrors);
    const foundErrors = Object.values(formErrors).filter((e) => e !== null).length;

    if (foundErrors) {
      if (foundErrors === 1) {
        setHasSingleError(true);
        const key = Object.keys(formErrors).find((key) => formErrors[key] !== null);
        scrollIntoView(formRefs[key]);
      } else {
        scrollIntoView(formRefs?.errorInfobox);
      }
    } else {
      setPreviewing(true);
      scrollIntoView(formRefs?.previewRef);
    }
  };

  const editForm = () => {
    setPreviewing(false);
  };

  const handleFormChange = (e: any, key: string = null, sendCopyChange: boolean) => {
    if (!formStarted) {
      trackFormStart("kontakt");
      setFormStarted(true);
    }
    if (sendCopyChange) {
      setFormState({
        ...formState,
        [key]: { ...formState[key], value: !formState[key].value },
      });
    } else {
      const { value } = e.target;
      setFormState({ ...formState, [key]: { ...formState[key], value: value } });
      setErrors({ ...errors, [key]: null });
    }
  };

  const handleErrorResponse = (): void => {
    setErrorSendMail(true);
    setDisplayForm(false);
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();
    let cleanFormState = {};
    for (const field in formState) {
      cleanFormState = { ...cleanFormState, [field]: formState[field].value };
    }
    setIsLoading(true);
    try {
      const res = await fetch("/api/sendmail", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(cleanFormState),
      });

      if (res?.ok) {
        setDisplayForm(false);
        trackFormSubmit("kontakt", formState["contactFormCaseType"].value);
      } else {
        handleErrorResponse();
      }

      setIsLoading(false);
      setPreviewing(false);
      setTimeout(() => {
        const reference = document.getElementById("formSendFeedback");
        reference.focus();
      }, 200);
      // eslint-disable-next-line sonarjs/no-ignored-exceptions
    } catch (_err) {
      handleErrorResponse();
      setIsLoading(false);
    }
  };

  const handleReCaptchaVerify = (token) => {
    if (!token) {
      return;
    }
    token && setIsValidToken(true);
  };

  const showFormFeedback: boolean = !displayForm && !errorSendMail;

  return (
    <Grid
      direction={"column"}
      className={`form-container`}
      data-contentful-entry-id={contentfulPreviewEntryId}
      data-contentful-field-id="name"
    >
      <GoogleReCaptcha onVerify={(token) => handleReCaptchaVerify(token)} />
      <FormSendFeedback
        formSendRef={formRefs?.sendFeedback}
        formErrorFeedback={<FormErrorFeedback messagePreset={formType} />}
        formSuccessFeedback={<FormSuccessFeedback messagePreset={formType} />}
        showSuccessFeedback={showFormFeedback}
        showErrorFeedback={errorSendMail}
      />
      <div id="preview"></div>
      {displayForm && previewing && (
        <div className={formStyles.formPreviewWrapper}>
          <FactCard
            heading="Kontrollera dina uppgifter"
            type={"blue"}
            headingVariant={"h2"}
            className={formStyles.formPreviewCard}
            data-blockname="faktaruta"
          >
            {/* Enables data suppression for hotjar */}
            <div className={"data-hj-suppress"}>
              <Text variant="span" weight="bold" className={`${formStyles.formPreviewQuestion}`}>
                Din e-postadress
              </Text>
              <Text variant="paragraph" className={formStyles.previewHelpText}>
                Kontrollera att du har stavat e-postadressen rätt - annars kan vi inte nå dig.
              </Text>
              <Text variant="paragraph" className={formStyles.formPreviewAnswer}>
                {formState?.email?.value || "-"}
              </Text>
              <>
                <Text variant="span" weight="bold" className={formStyles.formPreviewQuestion}>
                  Frågan handlar om
                </Text>
                <Text variant="paragraph" className={formStyles.formPreviewAnswer}>
                  {formState?.contactFormCaseType?.value || "-"}
                </Text>
              </>
              <Text variant="span" weight="bold" className={formStyles.formPreviewQuestion}>
                Beskriv hur vi kan hjälpa dig
              </Text>
              <Text variant="paragraph" className={formStyles.previewHelpText}>
                {formData.messageInputFieldDescription[formType]}
              </Text>
              <Text variant="paragraph" className={formStyles.formPreviewAnswer}>
                {formState?.contactFormMessage?.value || "-"}
              </Text>
            </div>
            <Button
              buttonType="secondary"
              onClick={() => editForm()}
              label={"Ändra"}
              className={formStyles.editButton}
              iconName="arrow_back"
            />
          </FactCard>
          <Checkbox
            inputRef={formRefs?.sendCopy}
            checked={formState?.sendCopy?.value}
            label="Ja tack! Skicka en kopia av meddelandet till min e&#8209;postadress."
            onChange={() => handleFormChange(null, "sendCopy", true)}
            id={formIds?.sendCopy}
            isWrapped
            onClick={() => handleFormChange(null, "sendCopy", true)}
            className={formStyles.sendCopyCheck}
          />
          <Button
            type="submit"
            buttonType="primary"
            onClick={
              isValidToken
                ? onSubmit
                : (e) => {
                    e.preventDefault();
                  }
            }
            label={isLoading ? "" : "Skicka"}
            className={clsx(formStyles.submitPreviewButton, { [formStyles.spinner]: isLoading })}
          />
        </div>
      )}
      {displayForm && !previewing && (
        <div className={`${formStyles.formBackground} ${formStyles.formWrapper}`}>
          <div className={formStyles.formHeadingWrapper}>
            <Heading id={formIds?.header} variant="h2" removeMargin>
              Ditt meddelande
            </Heading>
            <Text variant="paragraph" removeMargin>
              * Obligatoriskt fält
            </Text>
          </div>
          {
            <FormValidationFeedback
              formErrors={errors}
              formRefs={formRefs}
              hasSingleError={hasSingleError}
            />
          }
          <form aria-labelledby={formIds?.header} noValidate className={formStyles.form}>
            <div className={formStyles.formStep}>
              <div>
                <TextInput
                  label={"Din e-postadress *"}
                  description="Kontrollera att du har stavat e-postadressen rätt - annars kan vi inte nå dig."
                  inputRef={formRefs?.email}
                  required
                  id={formIds?.email}
                  autoComplete="email"
                  value={formState?.email?.value}
                  type={"email"}
                  maxLength={emailMaxInputLength}
                  width={"100%"}
                  fullWidth
                  error={errors?.email?.input}
                  onChange={(e) => handleFormChange(e, "email", false)}
                />
              </div>
            </div>
            <div className={formStyles.formStep}>
              {formData.showSubjectInputField[formType] && (
                <Select
                  inputRef={formRefs?.contactFormCaseType}
                  labelText={"Frågan handlar om *"}
                  required
                  placeholder="Välj ämne"
                  options={caseTypes}
                  error={errors?.contactFormCaseType?.input}
                  id={formIds?.contactFormCaseType}
                  selected={formState?.contactFormCaseType?.value || ""}
                  onChange={(e) => {
                    handleFormChange(e, "contactFormCaseType", false);
                    setErrors({ ...errors, ["contactFormCaseType"]: null });
                  }}
                  width={"100%"}
                />
              )}

              <div>
                <TextArea
                  textAreaRef={formRefs?.contactFormMessage}
                  required
                  data-testid="contactFormMessage"
                  label="Beskriv hur vi kan hjälpa dig *"
                  description={<p>{formData.messageInputFieldDescription[formType]}</p>}
                  id={formIds?.contactFormMessage}
                  value={formState?.contactFormMessage?.value}
                  name="contactFormMessage"
                  width={"100%"}
                  maxLength={messageMaxInputLength}
                  rows={8}
                  error={errors?.contactFormMessage?.input}
                  onChange={(e) => handleFormChange(e, "contactFormMessage", false)}
                />
              </div>
            </div>
            <StandardLink href={personuppgifterPath} text={"Så behandlar vi personuppgifter"} />
            <Button
              buttonType="primary"
              onClick={(e) => goToPreview(e)}
              label={"Nästa"}
              className={formStyles.submitButton}
            />
          </form>
        </div>
      )}
    </Grid>
  );
};
export default ContactForm;
